import React, { useMemo } from "react";
import { Bar, BarChart, CartesianGrid, XAxis, YAxis } from "recharts";
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@components/ui/chart";
import { Skeleton } from "@components/ui/skeleton";
import { ArrowDownRight, ArrowUpRight } from "lucide-react";
import { format, startOfDay, startOfMonth, startOfWeek } from "date-fns";

const getWeekStartDate = (date: Date): Date => {
  return startOfWeek(date, { weekStartsOn: 1 });
};

const getMonthStartDate = (date: Date): Date => {
  return startOfMonth(date);
};

interface TestCountChartProps {
  currentPeriodData?: Array<{
    date: Date;
    count: number;
    failedSensorChecks: number;
    successfulSensorChecks: number;
    soilTests: number;
  }> | null;
  previousPeriodData?: Array<{
    date: Date;
    count: number;
    failedSensorChecks: number;
    successfulSensorChecks: number;
    soilTests: number;
  }> | null;
  loading: boolean;
}

export const TestCountChart = ({
  currentPeriodData,
  previousPeriodData,
  loading,
}: TestCountChartProps) => {
  const sortedCurrentData = (currentPeriodData ?? [])
    .slice()
    .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

  const soilTestsCurrent = sortedCurrentData.reduce(
    (acc, item) => acc + item.soilTests,
    0
  );
  const soilTestsPrevious = (previousPeriodData ?? []).reduce(
    (acc, item) => acc + item.soilTests,
    0
  );
  const percentChange =
    soilTestsPrevious && soilTestsPrevious > 0
      ? ((soilTestsCurrent - soilTestsPrevious) / soilTestsPrevious) * 100
      : 0;
  const isPositive = percentChange >= 0;

  let processedData: Array<{ date: number; tests: number }> = [];
  let xAxisFormat = "dd MMM";
  let aggregationLevel: "day" | "week" | "month" = "day";

  if (sortedCurrentData.length > 1) {
    const firstDate = new Date(sortedCurrentData[0].date);
    const lastDate = new Date(
      sortedCurrentData[sortedCurrentData.length - 1].date
    );
    const timeSpanDays =
      (lastDate.getTime() - firstDate.getTime()) / (1000 * 60 * 60 * 24);

    // If range is greater than 26 weeks, show as months
    const WEEKS_THRESHOLD = 26;
    // Else if range is greater than 30 days, show as weeks, else days
    const DAYS_THRESHOLD = 30;

    if (timeSpanDays > WEEKS_THRESHOLD * 7) {
      // Aggregate by month
      aggregationLevel = "month";
      xAxisFormat = "MMM yyyy";
      const monthlyData = new Map<number, number>();
      sortedCurrentData.forEach((item) => {
        const monthStartDate = getMonthStartDate(new Date(item.date));
        const timestamp = monthStartDate.getTime();
        monthlyData.set(
          timestamp,
          (monthlyData.get(timestamp) ?? 0) + item.soilTests
        );
      });
      processedData = Array.from(monthlyData.entries())
        .map(([timestamp, count]) => ({ date: timestamp, tests: count }))
        .sort((a, b) => a.date - b.date);
    } else if (timeSpanDays > DAYS_THRESHOLD) {
      // Aggregate by week
      aggregationLevel = "week";
      xAxisFormat = "dd MMM";
      const weeklyData = new Map<number, number>();
      sortedCurrentData.forEach((item) => {
        const weekStartDate = getWeekStartDate(new Date(item.date));
        const timestamp = weekStartDate.getTime();
        weeklyData.set(
          timestamp,
          (weeklyData.get(timestamp) ?? 0) + item.soilTests
        );
      });
      processedData = Array.from(weeklyData.entries())
        .map(([timestamp, count]) => ({ date: timestamp, tests: count }))
        .sort((a, b) => a.date - b.date);
    } else {
      aggregationLevel = "day";
      processedData = sortedCurrentData
        .map((item) => {
          const date = startOfDay(new Date(item.date));

          return {
            date: date.getTime(),
            tests: item.soilTests,
          };
        })
        .sort((a, b) => a.date - b.date);
    }
  } else if (sortedCurrentData.length === 1) {
    processedData = sortedCurrentData.map((item) => ({
      date: new Date(item.date).getTime(),
      tests: item.soilTests,
    }));
  }

  const chartData = useMemo(
    () =>
      processedData.map((point) => ({
        date: point.date,
        tests: point.tests,
      })),
    [processedData]
  );

  const chartConfig = {
    tests: {
      label: "Soil tests",
      color: "#2563eb",
    },
  };

  const renderTooltipLabel = (value: unknown) => {
    if (typeof value !== "number") {
      return "";
    }
    const date = new Date(value);
    if (aggregationLevel === "week") {
      return `Week of ${format(date, "dd MMM yyyy")}`;
    }
    if (aggregationLevel === "month") {
      return format(date, "MMMM yyyy");
    }
    return format(date, "EEE, dd MMM yyyy");
  };

  return (
    <div className="flex h-full flex-col gap-4">
      <div className="flex items-center gap-3">
        <span className="text-2xl font-semibold">
          {soilTestsCurrent.toLocaleString()}
        </span>
        {soilTestsPrevious > 0 && (
          <span
            className={`flex items-center gap-1 text-sm font-medium ${
              isPositive ? "text-emerald-600" : "text-rose-500"
            }`}
          >
            {isPositive ? (
              <ArrowUpRight className="size-4" aria-hidden="true" />
            ) : (
              <ArrowDownRight className="size-4" aria-hidden="true" />
            )}
            {Math.abs(percentChange).toFixed(1)}%
          </span>
        )}
      </div>
      {loading ? (
        <Skeleton className="h-full min-h-[220px] w-full rounded-lg" />
      ) : (
        <ChartContainer
          config={chartConfig}
          className="h-full min-h-[220px] w-full flex-1"
        >
          <BarChart data={chartData}>
            <CartesianGrid vertical={false} strokeDasharray="3 3" />
            <XAxis
              dataKey="date"
              type="number"
              domain={["auto", "auto"]}
              tickLine={false}
              axisLine={false}
              tickFormatter={(value) =>
                format(new Date(Number(value)), xAxisFormat)
              }
            />
            <YAxis
              tickLine={false}
              axisLine={false}
              width={48}
              allowDecimals={false}
            />
            <ChartTooltip
              content={
                <ChartTooltipContent
                  formatter={(value) => (
                    <span className="font-medium">
                      {Number(value).toLocaleString()} tests
                    </span>
                  )}
                  labelFormatter={(value) => renderTooltipLabel(value)}
                />
              }
            />
            <Bar
              dataKey="tests"
              fill="var(--color-tests)"
              radius={[6, 6, 0, 0]}
              maxBarSize={48}
            />
          </BarChart>
        </ChartContainer>
      )}
    </div>
  );
};
